"use client";

import { useState } from "react";
import NumberFlow, { type Format } from "@number-flow/react";

import { useUpgradePlanFlow } from "@/features/billing/hooks/use-upgrade-plan-flow";
import { Icon } from "@/components/ui/icon";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useCreditBalance } from "@/features/billing/hooks/use-subscription";
import { CreditBreakdownModal } from "@/features/billing/components/credit-breakdown-modal";
import { UserMenu } from "@/components/layout/navbar/user-menu";
import { cn } from "@/lib/utils";
import type { IconName } from "@/components/ui/icon";

/* ── Credits meter ────────────────────────────────────────────────────── */

function CreditsMeter({
  expanded,
  isFreePlan,
  onUpgrade,
}: {
  expanded: boolean;
  isFreePlan: boolean;
  onUpgrade: () => void;
}) {
  const { data: credits } = useCreditBalance();
  const [showBreakdown, setShowBreakdown] = useState(false);

  if (!credits) return null;

  const balance = credits.balance;
  const total = credits.total_allocation;
  const unlimited = total <= 0;
  const remainingPercent = unlimited
    ? 100
    : Math.round((Math.min(balance, total) / total) * 100);
  const low = !unlimited && remainingPercent <= 15;
  // Labels always carry the exact figures; the card itself switches to
  // compact notation once the pair stops fitting on one line (the top plans
  // run into the millions).
  const exactBalance = balance.toLocaleString();
  const exactTotal = unlimited ? "∞" : total.toLocaleString();
  // `Format` is NumberFlow's narrowing of `Intl.NumberFormatOptions` (no
  // scientific/engineering notation), so one value feeds both the animated
  // figure and the plain `toLocaleString` copies.
  const compactNotation: Format | undefined =
    Math.max(balance, total) >= 100_000
      ? { notation: "compact", maximumFractionDigits: 1 }
      : undefined;
  const displayBalance = balance.toLocaleString(undefined, compactNotation);
  const displayTotal = unlimited
    ? "∞"
    : total.toLocaleString(undefined, compactNotation);

  const breakdown = (
    <CreditBreakdownModal
      open={showBreakdown}
      onOpenChange={setShowBreakdown}
      showUsage
    />
  );

  if (!expanded) {
    return (
      <>
        <FooterIconButton
          icon="IconCoin1"
          label={`${exactBalance} credits — view breakdown`}
          onClick={() => setShowBreakdown(true)}
          iconClassName="text-[#c2710a] dark:text-ai"
        />
        {isFreePlan && (
          <FooterIconButton
            icon="IconArrowUpCircle"
            label="Upgrade plan"
            onClick={onUpgrade}
            iconClassName="text-ai"
          />
        )}
        {breakdown}
      </>
    );
  }

  return (
    <>
      <div
        data-slot="sidebar-usage-card"
        className={cn(
          "relative overflow-hidden rounded-lg",
          "bg-surface-primary dark:bg-surface-elevated",
          "shadow-[inset_0_0_0_1px_var(--border-alpha),0_1px_2px_rgba(0,0,0,0.04)]",
        )}
      >
        {/* Warm brand wash rising from the bottom edge, under the meter,
            settling into a darker shade at the top of the card. */}
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 bg-[linear-gradient(to_top,color-mix(in_srgb,var(--ai)_18%,transparent),transparent_55%,rgba(0,0,0,0.22))]"
        />

        <button
          type="button"
          onClick={() => setShowBreakdown(true)}
          aria-label={`${exactBalance} of ${exactTotal} credits remaining — view breakdown`}
          title={`${exactBalance} of ${exactTotal} credits remaining`}
          className={cn(
            "relative flex w-full cursor-pointer flex-col gap-2 p-2.5 text-left",
            "outline-none transition-colors hover:bg-contrast/[0.025] focus-visible:ring-2 focus-visible:ring-ai/40",
          )}
        >
          <div className="flex w-full items-center justify-between gap-2">
            <span className="flex min-w-0 items-center gap-1.5">
              <Icon
                name="IconCoin1"
                size={14}
                className="shrink-0 text-[#c2710a] dark:text-ai"
              />
              <span className="truncate text-xs font-medium leading-4 text-heading">
                Credits
              </span>
            </span>
            {/* The figures keep their line; "Credits" truncates first. */}
            <span className="flex shrink-0 items-baseline gap-0.5 whitespace-nowrap text-xs leading-4 tabular-nums">
              <span className="relative inline-block font-semibold text-heading">
                {/* Invisible copy holds the width so the flex row never sees 0
                    while NumberFlow hydrates. */}
                <span
                  className="invisible select-none pointer-events-none"
                  aria-hidden="true"
                >
                  {displayBalance}
                </span>
                <NumberFlow
                  value={balance}
                  format={compactNotation}
                  className="absolute inset-0 flex items-center justify-end"
                />
              </span>
              <span className="text-contrast/40">/ {displayTotal}</span>
            </span>
          </div>
          <div className="h-1.5 w-full overflow-hidden rounded-full bg-contrast/8">
            <div
              className={cn(
                "h-full rounded-full transition-[width] duration-500 ease-out",
                low ? "bg-destructive" : "bg-ai",
                unlimited && "bg-[linear-gradient(90deg,var(--ai),color-mix(in_srgb,var(--ai)_40%,transparent))]",
              )}
              style={{
                width: `${remainingPercent}%`,
                minWidth: remainingPercent > 0 ? 4 : undefined,
              }}
            />
          </div>
        </button>

        {isFreePlan && (
          <div className="relative border-t border-contrast/6 p-1.5">
            <button
              type="button"
              onClick={onUpgrade}
              className={cn(
                "group/upgrade flex h-7 w-full cursor-pointer items-center justify-center gap-1 rounded-md",
                "bg-ai text-xs font-semibold text-[#1c1206]",
                "shadow-[inset_0_1px_0_rgba(255,255,255,0.35),0_1px_2px_rgba(0,0,0,0.12)]",
                "transition-[filter,transform] duration-150 hover:brightness-105 active:scale-[0.985]",
                "outline-none focus-visible:ring-2 focus-visible:ring-ai/50 focus-visible:ring-offset-1 focus-visible:ring-offset-surface-primary",
              )}
            >
              Upgrade
              <Icon
                name="IconArrowUpRight"
                size={12}
                className="transition-transform duration-150 group-hover/upgrade:-translate-y-px group-hover/upgrade:translate-x-px"
              />
            </button>
          </div>
        )}
      </div>
      {breakdown}
    </>
  );
}

/* ── Small icon-only action (tooltip carries the label) ───────────────── */

function FooterIconButton({
  icon,
  label,
  onClick,
  iconClassName,
}: {
  icon: IconName;
  label: string;
  onClick: () => void;
  iconClassName?: string;
}) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          type="button"
          onClick={onClick}
          aria-label={label}
          className={cn(
            "flex size-8 shrink-0 cursor-pointer items-center justify-center rounded-md",
            "text-contrast/45 transition-colors duration-150 hover:bg-contrast/4 hover:text-heading",
            "outline-none focus-visible:ring-2 focus-visible:ring-ai/40",
          )}
        >
          <Icon name={icon} size={16} className={cn("size-4", iconClassName)} />
        </button>
      </TooltipTrigger>
      <TooltipContent side="top" sideOffset={6}>
        {label}
      </TooltipContent>
    </Tooltip>
  );
}

/* ── Footer ───────────────────────────────────────────────────────────── */

/**
 * Desktop sidebar footer: credits meter (+ Upgrade on the free plan) above the
 * account row (avatar, name, role) pinned to the bottom-left. Settings,
 * Documentation and Support live in the account menu. On the icon rail
 * everything stacks into one column.
 */
export function SidebarFooterContent({ expanded }: { expanded: boolean }) {
  const { isFreePlan, openUpgrade, modals } = useUpgradePlanFlow();

  return (
    <div
      className={cn(
        "flex flex-col gap-2 px-3.5 py-3",
        !expanded && "items-center",
      )}
    >
      <CreditsMeter
        expanded={expanded}
        isFreePlan={isFreePlan}
        onUpgrade={openUpgrade}
      />

      <UserMenu appearance="sidebar" collapsed={!expanded} />

      {modals}
    </div>
  );
}
